/**
 * Finance capabilities (FIN-01 P1).
 *
 * This module is the single source of truth for the finance capability
 * matrix. It is intentionally PURE: it has no server-only dependencies
 * (no `@trpc/server`) so it can be imported from the client bundle, feature
 * flags, audit log writers, and server adapters alike.
 *
 * A "capability" is a high-level finance action expressed as a dotted string
 * (`finance.read`, `finance.write`, ...). Each capability maps 1:1 to the set
 * of roles allowed to perform it. The server-side adapter that translates a
 * denied capability into a tRPC FORBIDDEN error lives in
 * `server/modules/finance/supplierInvoices/permissions.ts`.
 */

export type FinanceRole = "admin" | "admin_manager" | "project_manager" | "technician";

export type FinanceCapability =
  | "finance.read"
  | "finance.write"
  | "finance.approve"
  | "finance.configure"
  | "finance.import"
  | "finance.export";

/**
 * Maps each capability to the set of roles allowed to perform it.
 *
 * Matrix (FIN-01 P1):
 * - admin:           all capabilities
 * - admin_manager:   all capabilities
 * - project_manager: read + export only
 * - technician:      no finance access (fail-closed)
 */
export const FINANCE_CAPABILITIES: Record<FinanceCapability, Set<FinanceRole>> = {
  "finance.read": new Set<FinanceRole>(["admin", "admin_manager", "project_manager"]),
  "finance.write": new Set<FinanceRole>(["admin", "admin_manager"]),
  "finance.approve": new Set<FinanceRole>(["admin", "admin_manager"]),
  "finance.configure": new Set<FinanceRole>(["admin", "admin_manager"]),
  "finance.import": new Set<FinanceRole>(["admin", "admin_manager"]),
  "finance.export": new Set<FinanceRole>(["admin", "admin_manager", "project_manager"]),
};

const CAPABILITY_NAMES = new Set<string>(Object.keys(FINANCE_CAPABILITIES));

/**
 * Type guard: returns true when `cap` is a known capability name.
 * Unknown strings resolve to `false` so callers fail-closed.
 */
export function isValidFinanceCapability(cap: string): cap is FinanceCapability {
  return CAPABILITY_NAMES.has(cap);
}

/**
 * Returns true when `role` is allowed to perform `capability`.
 * Unknown roles or unknown capabilities resolve to `false` (fail-closed).
 * Pure: never throws.
 */
export function hasCapability(role: FinanceRole, capability: FinanceCapability): boolean {
  const allowed = FINANCE_CAPABILITIES[capability];
  return Boolean(allowed) && allowed.has(role);
}
